import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Autocomplete,
  Paper,
  Tabs,
  Tab,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Divider,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  LISTING_STATUSES,
  FOOD_LISTING_SUBCATEGORIES,
  NONFOOD_LISTING_SUBCATEGORIES,
} from "../constants";
import { api } from "../api/client";
import {
  ConfirmDialog,
  EmptyState,
  PageContainer,
  PageHeader,
  StatusChip,
} from "../components";
import { CategoryOrderDialog } from "../components/vendors";
const API_BASE = process.env.REACT_APP_API_BASE || "/v1";
const API_KEY = process.env.REACT_APP_API_KEY;
const createInitialVariantOption = () => ({
  name: "",
  price: "",
});

const createInitialVariant = () => ({
  name: "",
  type: "addon_option",
  required: false,
  selectionMode: "single",
  price: "",
  options: [createInitialVariantOption()],
});

const normalizeVariantType = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
  if (["primary", "override", "primary_override"].includes(normalized)) {
    return "primary";
  }
  if (["addon_fixed", "add_on_fixed", "fixed"].includes(normalized)) {
    return "addon_fixed";
  }
  return "addon_option";
};

const hasOptionListType = (type) =>
  ["primary", "addon_option"].includes(normalizeVariantType(type));
const isSinglePriceType = (type) =>
  ["addon_fixed"].includes(normalizeVariantType(type));
const normalizeSelectionMode = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (["multiple", "multi", "checkbox", "many"].includes(normalized)) {
    return "multiple";
  }
  return "single";
};

const normalizeVariants = (variants) => {
  let source = variants;
  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      source = [];
    }
  }
  if (!Array.isArray(source)) return [];
  return source.map((variant) => {
    const type = normalizeVariantType(variant?.type);
    const options = Array.isArray(variant?.options)
      ? variant.options.map((option) => ({
          name: option?.name ? String(option.name) : "",
          price:
            option?.price === undefined || option?.price === null
              ? ""
              : String(option.price),
        }))
      : [];
    return {
      name: variant?.name ? String(variant.name) : "",
      type,
      required: Boolean(variant?.required),
      selectionMode: normalizeSelectionMode(variant?.selectionMode),
      price: isSinglePriceType(type)
        ? variant?.price === undefined || variant?.price === null
          ? ""
          : String(variant.price)
        : "",
      options: hasOptionListType(type)
        ? options.length
          ? options
          : [createInitialVariantOption()]
        : [],
    };
  });
};

const sanitizeVariantsForPayload = (hasVariants, variants) => {
  if (!hasVariants || !Array.isArray(variants)) return [];
  return variants
    .map((variant) => {
      const name = variant?.name ? String(variant.name).trim() : "";
      const type = normalizeVariantType(variant?.type);
      const required = Boolean(variant?.required);
      const selectionMode = normalizeSelectionMode(variant?.selectionMode);
      const price =
        variant?.price === "" || variant?.price === null
          ? null
          : Number(variant.price);
      const options = hasOptionListType(type)
        ? (Array.isArray(variant?.options) ? variant.options : [])
            .map((option) => {
              const optionName = option?.name ? String(option.name).trim() : "";
              const numericPrice =
                option?.price === "" || option?.price === null
                  ? 0
                  : Number(option.price);
              return {
                name: optionName,
                price: Number.isFinite(numericPrice) ? numericPrice : 0,
              };
            })
            .filter((option) => option.name)
        : [];
      return {
        name,
        type:
          type === "primary"
            ? "Primary"
            : type === "addon_fixed"
              ? "AddOnFixed"
              : "AddOnOption",
        pricingMode: type === "primary" ? "override" : "add_on",
        required,
        ...(hasOptionListType(type)
          ? {
              selectionMode,
              selectionControl:
                selectionMode === "multiple" ? "checkbox" : "radio",
            }
          : {}),
        ...(isSinglePriceType(type)
          ? { price: Number.isFinite(price) ? price : null }
          : { options }),
      };
    })
    .filter((variant) => variant.name);
};

const dedupeAndSortNames = (names) => {
  const lookup = new Map();
  names.forEach((name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (!lookup.has(key)) {
      lookup.set(key, trimmed);
    }
  });
  return Array.from(lookup.values()).sort((a, b) => a.localeCompare(b));
};

const initialListing = {
  name: "",
  type: "item",
  subCategory: "",
  itemType: "food_item",
  preorder: false,
  quantity: "",
  price: "",
  currency: "PHP",
  status: "active",
  components: [],
  featuredPhotoUrl: "",
  supplementalPhotoUrls: ["", "", ""],
  tag: "",
  description: "",
  vendorId: "",
  hasVariants: false,
  variants: [],
};

const initialComponent = {
  componentListingId: "",
  quantityRequired: "",
  minimumRequired: "",
  addonsRequired: "",
  addonsOptional: "",
};

export default function ListingsPage({
  listings,
  setListings,
  setVendorListings,
  vendorListings,
  listingModalRequest,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [form, setForm] = useState(initialListing);
  const [componentDraft, setComponentDraft] = useState(initialComponent);
  const [addMore, setAddMore] = useState(false);
  const listingNameRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoMode, setPhotoMode] = useState("browse");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoTarget, setPhotoTarget] = useState({
    type: "featured",
    index: null,
  });
  const [cloneOpen, setCloneOpen] = useState(false);
  const [vendorFilterId, setVendorFilterId] = useState("");
  const [vendorFilterInput, setVendorFilterInput] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [vendorFilterOptions, setVendorFilterOptions] = useState([]);
  const [vendorFilterLoading, setVendorFilterLoading] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneForm, setCloneForm] = useState({
    name: "",
    price: "",
    currency: "PHP",
  });
  const [allVendors, setAllVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorInput, setVendorInput] = useState("");
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorOptionsLoading, setVendorOptionsLoading] = useState(false);
  const [activeVendor, setActiveVendor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [orderedListings, setOrderedListings] = useState([]);
  const [draggingListingId, setDraggingListingId] = useState(null);
  const [dragOverListingId, setDragOverListingId] = useState(null);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [categoryOrderOpen, setCategoryOrderOpen] = useState(false);
  const [variantNameSuggestions, setVariantNameSuggestions] = useState([]);
  const [optionNameSuggestions, setOptionNameSuggestions] = useState([]);
  const [variantObjectSuggestions, setVariantObjectSuggestions] = useState([]);
  const [optionPriceLookup, setOptionPriceLookup] = useState(new Map());
  const [variantSuggestionsLoading, setVariantSuggestionsLoading] =
    useState(false);
  const [remoteVariantNamesLoading, setRemoteVariantNamesLoading] =
    useState(false);
  const [remoteVariantNamesFetchError, setRemoteVariantNamesFetchError] =
    useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const isValid = useMemo(() => form.name.trim(), [form]);
  const hasInvalidVariantPrice = useMemo(
    () =>
      form.hasVariants &&
      form.variants.some((variant) => {
        if (!isSinglePriceType(variant?.type)) return false;
        const price = variant?.price;
        return (
          price === "" || price === null || !Number.isFinite(Number(price))
        );
      }),
    [form.hasVariants, form.variants],
  );
  const vendorLookup = useMemo(() => {
    const map = new Map();
    [...allVendors, ...vendorOptions, ...vendorFilterOptions].forEach(
      (vendor) => {
        if (vendor?.id && !map.has(vendor.id)) {
          map.set(vendor.id, vendor);
        }
      },
    );
    return map;
  }, [allVendors, vendorOptions, vendorFilterOptions]);
  const selectedVendorFromForm = useMemo(() => {
    if (!form.vendorId) return null;
    return vendorLookup.get(form.vendorId) || null;
  }, [form.vendorId, vendorLookup]);
  // Sequence in edit mode:
  // 1) resolve/preselect vendor
  // 2) then load sub-category options from that resolved vendor
  const derivedSubCategoryOptions = useMemo(() => {
    const resolvedVendor = selectedVendorFromForm || activeVendor;
    if (editingId && form.vendorId && !resolvedVendor) {
      return [];
    }
    if (resolvedVendor?.category === "restaurant/eatery") {
      return FOOD_LISTING_SUBCATEGORIES;
    }
    if (resolvedVendor) {
      return NONFOOD_LISTING_SUBCATEGORIES;
    }
    return [...FOOD_LISTING_SUBCATEGORIES, ...NONFOOD_LISTING_SUBCATEGORIES];
  }, [selectedVendorFromForm, activeVendor, editingId, form.vendorId]);
  const selectedVendor = form.vendorId
    ? vendorLookup.get(form.vendorId) ||
      (activeVendor && activeVendor.id === form.vendorId
        ? activeVendor
        : null)
    : null;
  const selectedVendorFilter = vendorFilterId
    ? vendorLookup.get(vendorFilterId) || null
    : null;
  const vendorFilterList = vendorFilterInput.trim()
    ? vendorFilterOptions
    : allVendors;
  const vendorSelectList = vendorInput.trim() ? vendorOptions : allVendors;
  // const listingCategoryFilterOptions = useMemo(
  //   () =>
  //     dedupeAndSortNames([
  //       ...FOOD_LISTING_SUBCATEGORIES,
  //       ...NONFOOD_LISTING_SUBCATEGORIES,
  //     ]),
  //   [],
  // );
  const [listingCategoryFilterOptions, setListingCategoryFilterOptions] =
    useState(() => []);

  const handleChange = (field) => (event) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleComponentChange = (field) => (event) =>
    setComponentDraft((prev) => ({ ...prev, [field]: event.target.value }));
  const setVariantField = (variantIndex, field, value) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, index) => {
        if (index !== variantIndex) return variant;
        if (field === "type") {
          const nextType = normalizeVariantType(value);
          return {
            ...variant,
            type: nextType,
            price: isSinglePriceType(nextType) ? variant.price || "" : "",
            options: hasOptionListType(nextType)
              ? Array.isArray(variant.options) && variant.options.length
                ? variant.options
                : [createInitialVariantOption()]
              : [],
          };
        }
        return { ...variant, [field]: value };
      }),
    }));
  };
  const setVariantOptionField = (variantIndex, optionIndex, field, value) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, index) => {
        if (index !== variantIndex) return variant;
        const nextOptions = (
          Array.isArray(variant.options) ? variant.options : []
        ).map((option, nestedIndex) =>
          nestedIndex === optionIndex ? { ...option, [field]: value } : option,
        );
        return {
          ...variant,
          options: nextOptions,
        };
      }),
    }));
  };
  const handleVariantsToggle = (event) => {
    const checked = event.target.checked;
    setForm((prev) => ({
      ...prev,
      hasVariants: checked,
      variants: checked
        ? prev.variants.length
          ? prev.variants
          : [createInitialVariant()]
        : prev.variants,
    }));
  };
  const handleAddVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [...prev.variants, createInitialVariant()],
    }));
  };
  const handleRemoveVariant = (variantIndex) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, index) => index !== variantIndex),
    }));
  };
  const handleVariantChange = (variantIndex, field) => (event) => {
    const value = event.target.value;
    setVariantField(variantIndex, field, value);
  };
  const handleAddVariantOption = (variantIndex) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex
          ? {
              ...variant,
              options: [
                ...(Array.isArray(variant.options) ? variant.options : []),
                createInitialVariantOption(),
              ],
            }
          : variant,
      ),
    }));
  };
  const handleRemoveVariantOption = (variantIndex, optionIndex) => {
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, index) =>
        index === variantIndex
          ? {
              ...variant,
              options: (Array.isArray(variant.options)
                ? variant.options
                : []
              ).filter((_, nestedIndex) => nestedIndex !== optionIndex),
            }
          : variant,
      ),
    }));
  };
  const handleVariantOptionChange =
    (variantIndex, optionIndex, field) => (event) => {
      const value = event.target.value;
      setVariantOptionField(variantIndex, optionIndex, field, value);
    };
  useEffect(() => {
    let active = true;
    const isSortEnabled =
      Boolean(vendorFilterId) && Boolean(categoryFilter.trim());

    const effectivePage = isSortEnabled ? 1 : page + 1;
    const effectiveLimit = isSortEnabled ? 1000 : rowsPerPage;
    api
      .listCatalogListings({
        search: searchTerm.trim() || undefined,
        vendorId: vendorFilterId || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
        page: effectivePage,
        limit: effectiveLimit,
      })
      .then((res) => {
        if (!active) return;
        const next = res?.data?.listings || [];
        setListings(next);
        setTotalCount(res?.pagination?.total || 0);
      })
      .catch((err) => console.error(err));
    return () => {
      active = false;
    };
  }, [
    setListings,
    searchTerm,
    vendorFilterId,
    categoryFilter,
    statusFilter,
    page,
    rowsPerPage,
  ]);
  useEffect(() => {
    setPage(0);
  }, [searchTerm, vendorFilterId, categoryFilter, statusFilter]);
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, rowsPerPage, searchTerm, vendorFilterId, categoryFilter, statusFilter]);

  useEffect(() => {
    let active = true;
    if (!vendorFilterId) {
      setListingCategoryFilterOptions([]);
      setCategoryFilter("");
      // The category-order dialog is scoped to a vendor; clearing the filter
      // leaves it with nothing to edit.
      setCategoryOrderOpen(false);
      return undefined;
    }
    api
      .listCatalogListings({
        vendorId: vendorFilterId,
        page: 1,
        limit: 1000,
      })
      .then((res) => {
        if (!active) return;
        const next = res?.data?.listings || [];
        const categories = next
          .map((listing) => listing?.subCategory || "")
          .filter(Boolean);
        setListingCategoryFilterOptions(dedupeAndSortNames(categories));
      })
      .catch((err) => console.error(err));
    return () => {
      active = false;
    };
  }, [vendorFilterId]);
  useEffect(() => {
    let active = true;
    if (vendorListings.length === 0 && setVendorListings) {
      api
        .listCatalogVendorListings({ page: 1, limit: 1000 })
        .then((res) => {
          if (!active) return;
          const next = res?.data?.vendorListings || [];
          setVendorListings(next);
        })
        .catch((err) => console.error(err));
    }
    return () => {
      active = false;
    };
  }, [vendorListings.length, setVendorListings]);
  useEffect(() => {
    if (!listingModalRequest?.vendorId) return;
    setEditingId(null);
    setForm({
      ...initialListing,
      vendorId: listingModalRequest.vendorId,
    });
    setComponentDraft(initialComponent);
    setAddMore(false);
    setFormOpen(true);
  }, [listingModalRequest]);
  useEffect(() => {
    if (!editingId || form.vendorId) return;
    const existingLink = (vendorListings || []).find(
      (link) => link.listingId === editingId,
    );
    if (existingLink?.vendorId) {
      setForm((prev) => ({ ...prev, vendorId: existingLink.vendorId }));
    }
  }, [editingId, vendorListings, form.vendorId]);
  useEffect(() => {
    if (!form.vendorId) {
      setActiveVendor(null);
      return;
    }
    const match = vendorLookup.get(form.vendorId);
    if (match) {
      setActiveVendor(match);
    }
  }, [form.vendorId, vendorLookup]);
  const loadVendorById = async (vendorId) => {
    const pageSize = 500;
    const maxPages = 100;
    let pageIndex = 1;

    while (pageIndex <= maxPages) {
      const res = await api.listCatalogVendors({
        page: pageIndex,
        limit: pageSize,
      });
      const batch = res?.data?.vendors || [];
      const matched = batch.find((vendor) => vendor?.id === vendorId);
      if (matched) return matched;
      if (batch.length < pageSize) break;
      pageIndex += 1;
    }

    return null;
  };
  useEffect(() => {
    let active = true;
    const fetchAllVendors = async () => {
      const pageSize = 500;
      const maxPages = 100;
      let pageIndex = 1;
      let collected = [];
      let total = null;

      while (pageIndex <= maxPages) {
        const res = await api.listCatalogVendors({
          page: pageIndex,
          limit: pageSize,
        });
        const batch = res?.data?.vendors || [];
        collected = [...collected, ...batch];
        const paginationTotal = res?.pagination?.total;
        if (paginationTotal !== undefined && paginationTotal !== null) {
          total = paginationTotal;
          if (collected.length >= total) break;
        }
        if (batch.length < pageSize) break;
        pageIndex += 1;
      }

      return collected;
    };

    setVendorsLoading(true);
    fetchAllVendors()
      .then((list) => {
        if (!active) return;
        setAllVendors(list);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (!active) return;
        setVendorsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    const term = vendorFilterInput.trim();
    if (!term) {
      setVendorFilterOptions([]);
      setVendorFilterLoading(false);
      return undefined;
    }
    const timeout = setTimeout(() => {
      setVendorFilterLoading(true);
      api
        .listCatalogVendors({ search: term })
        .then((res) => {
          if (!active) return;
          setVendorFilterOptions(res?.data?.vendors || []);
        })
        .catch((err) => console.error(err))
        .finally(() => {
          if (!active) return;
          setVendorFilterLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [vendorFilterInput]);
  useEffect(() => {
    let active = true;
    const term = vendorInput.trim();
    if (!term) {
      setVendorOptions([]);
      setVendorOptionsLoading(false);
      return undefined;
    }
    const timeout = setTimeout(() => {
      setVendorOptionsLoading(true);
      api
        .listCatalogVendors({ search: term })
        .then((res) => {
          if (!active) return;
          setVendorOptions(res?.data?.vendors || []);
        })
        .catch((err) => console.error(err))
        .finally(() => {
          if (!active) return;
          setVendorOptionsLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [vendorInput]);
  const loadRemoteVariantNames = async () => {
    setRemoteVariantNamesLoading(true);
    setRemoteVariantNamesFetchError(null);
    try {
      const res = await api.loadCatalogVariantNames();
      const variants = res?.data?.variants || [];

      const variantNames = [];
      const optionNames = [];
      const collectedVariants = [];
      const optionPriceMap = new Map();

      variants.forEach((variant) => {
        if (variant?.name) {
          variantNames.push(variant.name);
          collectedVariants.push(variant);
          (variant?.options || []).forEach((option) => {
            if (option?.name) {
              optionNames.push(option.name);
              const key = option.name.trim().toLowerCase();
              if (
                !optionPriceMap.has(key) &&
                option.price !== "" &&
                option.price !== null &&
                option.price !== undefined
              ) {
                optionPriceMap.set(key, option.price);
              }
            }
          });
        }
      });

      setVariantNameSuggestions(dedupeAndSortNames(variantNames));
      setOptionNameSuggestions(dedupeAndSortNames(optionNames));
      setVariantObjectSuggestions(collectedVariants);
      setOptionPriceLookup(optionPriceMap);
    } catch (error) {
      console.error(error);
      setRemoteVariantNamesFetchError(
        error?.message || "Failed to load variant names",
      );
    } finally {
      setRemoteVariantNamesLoading(false);
    }
  };

  useEffect(() => {
    if (!formOpen) return;
    loadRemoteVariantNames();
  }, [formOpen]);

  const filteredListings = listings;
  const isSortEnabled =
    Boolean(vendorFilterId) && Boolean(categoryFilter.trim());

  useEffect(() => {
    if (!isSortEnabled) {
      setOrderedListings([]);
      return;
    }
    setOrderedListings(filteredListings);
  }, [filteredListings, isSortEnabled]);

  const persistListingOrder = async (nextListings) => {
    if (!isSortEnabled) return;
    setReorderSaving(true);
    try {
      await api.reorderCatalogVendorListings({
        vendorId: vendorFilterId,
        orderedListingIds: nextListings.map((item) => item.id),
      });
    } catch (error) {
      console.error(error);
      setOrderedListings(filteredListings);
    } finally {
      setReorderSaving(false);
    }
  };

  const moveListing = (sourceId, targetId) => {
    if (!isSortEnabled || sourceId === targetId) return;
    const current = [...orderedListings];
    const fromIndex = current.findIndex((item) => item.id === sourceId);
    const toIndex = current.findIndex((item) => item.id === targetId);
    if (fromIndex === -1 || toIndex === -1) return;
    const [moved] = current.splice(fromIndex, 1);
    current.splice(toIndex, 0, moved);
    setOrderedListings(current);
    persistListingOrder(current);
  };

  const displayedListings = isSortEnabled ? orderedListings : filteredListings;

  const handleAutoSort = () => {
    if (!isSortEnabled) return;
    const next = [...orderedListings].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
    );
    setOrderedListings(next);
    persistListingOrder(next);
  };

  const getFilteredVariantNames = (inputValue) => {
    if (!inputValue.trim()) return variantNameSuggestions;
    const term = inputValue.trim().toLowerCase();
    return variantNameSuggestions.filter((name) =>
      name.toLowerCase().includes(term),
    );
  };

  const getFilteredOptionNames = (inputValue) => {
    if (!inputValue.trim()) return optionNameSuggestions;
    const term = inputValue.trim().toLowerCase();
    return optionNameSuggestions.filter((name) =>
      name.toLowerCase().includes(term),
    );
  };

  const handleOptionNameSelected = (
    variantIndex,
    optionIndex,
    selectedName,
  ) => {
    if (!editingId) return;
    const key = selectedName.trim().toLowerCase();
    const price = optionPriceLookup.get(key);
    if (price !== undefined) {
      setVariantOptionField(variantIndex, optionIndex, "price", price);
    }
  };

  const handleVariantNameSelected = (variantIndex, selectedName) => {
    if (!editingId) return;
    const matchingVariants = variantObjectSuggestions.filter(
      (v) => v.name.trim().toLowerCase() === selectedName.trim().toLowerCase(),
    );
    if (matchingVariants.length > 0) {
      const firstMatch = matchingVariants[0];
      if (firstMatch.options && firstMatch.options.length > 0) {
        setForm((prev) => ({
          ...prev,
          variants: prev.variants.map((variant, index) =>
            index === variantIndex
              ? {
                  ...variant,
                  options: firstMatch.options.map((opt) => ({
                    name: opt.name || "",
                    price:
                      opt.price !== "" && opt.price !== null ? opt.price : "",
                  })),
                }
              : variant,
          ),
        }));
      }
    }
  };

  const handleAddComponent = () => {
    if (!componentDraft.componentListingId.trim()) return;
    const component = {
      componentListingId: componentDraft.componentListingId.trim(),
      quantityRequired:
        componentDraft.quantityRequired.trim() === ""
          ? null
          : Number(componentDraft.quantityRequired),
      minimumRequired:
        componentDraft.minimumRequired.trim() === ""
          ? null
          : Number(componentDraft.minimumRequired),
      addonsRequired: componentDraft.addonsRequired
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      addonsOptional: componentDraft.addonsOptional
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    };
    setForm((prev) => ({
      ...prev,
      components: [...prev.components, component],
    }));
    setComponentDraft(initialComponent);
  };

  const openCloneDialog = (listing) => {
    setCloneSource(listing);
    setCloneForm({
      name: `${listing.name} copy`,
      price: listing.price ?? "",
      currency: listing.currency ?? "PHP",
    });
    setCloneOpen(true);
  };

  const handleCloneChange = (field) => (event) =>
    setCloneForm((prev) => ({ ...prev, [field]: event.target.value }));

  const handleCloneSave = async () => {
    if (!cloneSource || !cloneForm.name.trim() || cloneForm.price === "")
      return;
    setLoading(true);
    try {
      const payload = {
        name: cloneForm.name.trim(),
        type: cloneSource.type,
        subCategory: cloneSource.subCategory || null,
        itemType: cloneSource.itemType || "food_item",
        preorder: Boolean(cloneSource.preorder),
        quantity: cloneSource.quantity ?? null,
        price: Number(cloneForm.price),
        currency: cloneForm.currency,
        status: cloneSource.status,
        components: Array.isArray(cloneSource.components)
          ? cloneSource.components
          : [],
        featuredPhotoUrl: cloneSource.featuredPhotoUrl || null,
        supplementalPhotoUrls: Array.isArray(cloneSource.supplementalPhotoUrls)
          ? cloneSource.supplementalPhotoUrls.filter(Boolean).slice(0, 3)
          : [],
        tag: cloneSource.tag || null,
        description: cloneSource.description || null,
        variants: cloneSource.variants || [],
      };
      const res = await api.createCatalogListing(payload);
      const newListing = res?.data;
      if (newListing) {
        setListings((prev) => [newListing, ...prev]);
      }
      setCloneOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getComponentLabel = (component) => {
    if (component.quantityRequired) return `qty ${component.quantityRequired}`;
    if (component.minimumRequired) return `min ${component.minimumRequired}`;
    return "-";
  };

  const openPhotoDialog = (target) => {
    setPhotoTarget(target);
    setPhotoMode("browse");
    setPhotoFile(null);
    setPhotoUrl("");
    setPhotoDialogOpen(true);
  };

  const buildPhotoUrl = (value) => {
    if (!value) return "";
    if (/^data:/i.test(value)) return value;
    // if (/^https?:\/\//i.test(value)) {
    //   if (!API_KEY || value.includes("api_key=")) return value;
    //   const separator = value.includes("?") ? "&" : "?";
    //   return `${value}${separator}api_key=${encodeURIComponent(API_KEY)}`;
    // }
    if (
      value.toString().includes("http://") ||
      value.toString().includes("https://")
    )
      return value;
    const url = `${API_BASE}/uploads/file?key=${encodeURIComponent(value)}`;
    if (!API_KEY) return url;
    return `${url}&api_key=${encodeURIComponent(API_KEY)}`;
  };

  const applyPhotoPath = (path) => {
    if (photoTarget.type === "featured") {
      setForm((prev) => ({ ...prev, featuredPhotoUrl: path }));
      return;
    }
    if (photoTarget.type === "supplemental" && photoTarget.index !== null) {
      setForm((prev) => {
        const next = [...prev.supplementalPhotoUrls];
        next[photoTarget.index] = path;
        return { ...prev, supplementalPhotoUrls: next };
      });
    }
  };

  const handleUploadFile = async () => {
    if (!photoFile) return;
    setPhotoUploading(true);
    try {
      const res = await api.uploadCatalogPhotoFile({
        file: photoFile,
        photoType:
          photoTarget.type === "featured" ? "featured" : "supplemental",
      });
      const path = res?.data?.path;
      if (path) {
        applyPhotoPath(path);
        setPhotoDialogOpen(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleImportUrl = async () => {
    if (!photoUrl.trim()) return;
    setPhotoUploading(true);
    try {
      const res = await api.importCatalogPhotoUrl({
        sourceUrl: photoUrl.trim(),
        photoType:
          photoTarget.type === "featured" ? "featured" : "supplemental",
      });
      const path = res?.data?.path;
      if (path) {
        applyPhotoPath(path);
        setPhotoDialogOpen(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPhotoUploading(false);
    }
  };
  const syncVendorLink = async (listingId, vendorId) => {
    if (!setVendorListings) return;
    const existingLinks = (vendorListings || []).filter(
      (link) => link.listingId === listingId,
    );
    if (!vendorId) {
      if (!existingLinks.length) return;
      await Promise.all(
        existingLinks.map((link) => api.deleteCatalogVendorListing(link.id)),
      );
      setVendorListings((prev) =>
        prev.filter((link) => link.listingId !== listingId),
      );
      return;
    }
    const existingLink = existingLinks[0];
    if (existingLink && existingLink.vendorId === vendorId) return;
    if (existingLinks.length) {
      await Promise.all(
        existingLinks.map((link) => api.deleteCatalogVendorListing(link.id)),
      );
      setVendorListings((prev) =>
        prev.filter((link) => link.listingId !== listingId),
      );
    }
    const linkRes = await api.createCatalogVendorListing({
      vendorId,
      listingId,
      status: existingLink?.status || "active",
    });
    const link = linkRes?.data;
    if (link) {
      setVendorListings((prev) => [link, ...prev]);
    }
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isValid || hasInvalidVariantPrice) return;
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        subCategory: form.subCategory || null,
        itemType: form.itemType || "food_item",
        preorder: Boolean(form.preorder),
        quantity: form.quantity === "" ? null : Number(form.quantity),
        price: Number(form.price),
        currency: form.currency,
        status: form.status,
        components: form.type === "package" ? form.components : [],
        featuredPhotoUrl: form.featuredPhotoUrl.trim() || null,
        supplementalPhotoUrls: form.supplementalPhotoUrls
          .map((url) => url.trim())
          .filter(Boolean)
          .slice(0, 3),
        tag: form.tag.trim() || null,
        description: form.description.trim() || null,
        variants: sanitizeVariantsForPayload(form.hasVariants, form.variants),
      };
      if (editingId) {
        const res = await api.updateCatalogListing(editingId, payload);
        const updated = res?.data;
        if (updated) {
          setListings((prev) =>
            prev.map((l) => (l.id === editingId ? updated : l)),
          );
        }
        await syncVendorLink(editingId, form.vendorId);
      } else {
        const res = await api.createCatalogListing(payload);
        const newListing = res?.data;
        if (newListing) {
          setListings((prev) => [newListing, ...prev]);
          if (form.vendorId) {
            await syncVendorLink(newListing.id, form.vendorId);
          }
        }
      }

      if (editingId) {
        setForm(initialListing);
        setComponentDraft(initialComponent);
        setEditingId(null);
        setFormOpen(false);
        setAddMore(false);
      } else {
        setForm((prev) => ({ ...prev, name: "", description: "" }));
        setComponentDraft(initialComponent);
        setEditingId(null);
        setTimeout(() => {
          if (listingNameRef.current) {
            listingNameRef.current.focus();
          }
        }, 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const handleEdit = async (listing) => {
    let existingLink = (vendorListings || []).find(
      (link) => link.listingId === listing.id,
    );

    // Cache miss: the initial vendorListings fetch is capped at 1000 rows,
    // so on large catalogs the link for this listing may not be in the
    // cache. The backend has no listingId filter on /vendor-listings, but
    // search matches listing.name (iLike); we then verify by listing.id.
    if (!existingLink && listing?.name) {
      try {
        const res = await api.listCatalogVendorListings({
          search: listing.name,
          limit: 100,
        });
        const links = res?.data?.vendorListings || [];
        existingLink = links.find((link) => link.listingId === listing.id);
        if (existingLink && setVendorListings) {
          setVendorListings((prev) =>
            prev.some((link) => link.id === existingLink.id)
              ? prev
              : [existingLink, ...prev],
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }

    const vendorId = existingLink?.vendorId || "";
    let vendor = vendorId ? vendorLookup.get(vendorId) || null : null;

    if (vendorId && !vendor) {
      try {
        vendor = await loadVendorById(vendorId);
        if (vendor) {
          setAllVendors((prev) =>
            prev.some((item) => item.id === vendor.id)
              ? prev
              : [vendor, ...prev],
          );
        }
      } catch (error) {
        console.error(error);
      }
    }
    setAddMore(false);
    setEditingId(listing.id);
    setActiveVendor(vendor);
    setVendorInput("");
    const normalizedVariants = normalizeVariants(listing.variants);
    setForm({
      name: listing.name || "",
      type: listing.type || "item",
      subCategory: listing.subCategory || "",
      itemType: listing.itemType || "food_item",
      preorder: Boolean(listing.preorder),
      quantity:
        listing.quantity === null || listing.quantity === undefined
          ? ""
          : String(listing.quantity),
      price:
        listing.price === null || listing.price === undefined
          ? ""
          : String(listing.price),
      currency: listing.currency || "PHP",
      status: listing.status || "active",
      components: Array.isArray(listing.components) ? listing.components : [],
      featuredPhotoUrl: listing.featuredPhotoUrl || "",
      supplementalPhotoUrls: [0, 1, 2].map(
        (index) => listing.supplementalPhotoUrls?.[index] || "",
      ),
      tag: listing.tag || "",
      description: listing.description || "",
      vendorId: vendorId,
      hasVariants: normalizedVariants.length > 0,
      variants: normalizedVariants,
    });
    setComponentDraft(initialComponent);
    setFormOpen(true);
  };
  const requestDelete = (listing) => {
    setDeleteError(null);
    setPendingDelete(listing);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const listingId = pendingDelete.id;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteCatalogListing(listingId);
      setListings((prev) => prev.filter((l) => l.id !== listingId));
      if (setVendorListings) {
        const linksToRemove = (vendorListings || []).filter(
          (link) => link.listingId === listingId,
        );
        if (linksToRemove.length) {
          await Promise.all(
            linksToRemove.map((link) =>
              api.deleteCatalogVendorListing(link.id),
            ),
          );
          setVendorListings((prev) =>
            prev.filter((l) => l.listingId !== listingId),
          );
        }
      }
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(err?.message ?? "Failed to delete listing.");
    } finally {
      setDeleting(false);
    }
  };
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(new Set(displayedListings.map((l) => l.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkUpdating(true);
    try {
      await Promise.all(
        [...selectedIds].map((id) =>
          api.updateCatalogListing(id, { status: bulkStatus }),
        ),
      );
      setListings((prev) =>
        prev.map((l) =>
          selectedIds.has(l.id) ? { ...l, status: bulkStatus } : l,
        ),
      );
      setSelectedIds(new Set());
      setBulkStatus("");
    } catch (error) {
      console.error(error);
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleOpenNew = () => {
    setEditingId(null);
    setForm(initialListing);
    setComponentDraft(initialComponent);
    setAddMore(false);
    setFormOpen(true);
  };
  const handleCloseForm = () => {
    setEditingId(null);
    setForm(initialListing);
    setComponentDraft(initialComponent);
    setAddMore(false);
    setFormOpen(false);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Listings"
        subtitle="Manage menu items and packages across vendors."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={handleOpenNew}
          >
            Add listing
          </Button>
        }
      />

      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        fullScreen={isMobile}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{editingId ? "Edit listing" : "Add listing"}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 0.5 }}>
            <Grid container spacing={1.5}>
              {/* === BASIC INFO === */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1">Basic information</Typography>
                <Divider sx={{ mt: 1 }} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  options={vendorSelectList}
                  value={selectedVendor}
                  onChange={(_, value) => {
                    setForm((prev) => ({
                      ...prev,
                      vendorId: value?.id || "",
                    }));
                    setActiveVendor(value || null);
                  }}
                  inputValue={vendorInput}
                  onInputChange={(_, value) => setVendorInput(value)}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  filterOptions={(options) => options}
                  loading={vendorOptionsLoading || vendorsLoading}
                  noOptionsText={
                    vendorInput.trim()
                      ? "No vendors found"
                      : "No vendors available"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Vendor"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Listing name"
                  value={form.name}
                  onChange={handleChange("name")}
                  inputRef={listingNameRef}
                />
              </Grid>
              {/* <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={form.type}
                    onChange={handleChange("type")}
                  >
                    {LISTING_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid> */}
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status}
                    onChange={handleChange("status")}
                  >
                    {LISTING_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  freeSolo
                  options={derivedSubCategoryOptions}
                  value={form.subCategory || null}
                  onChange={(_, value) =>
                    setForm((prev) => ({ ...prev, subCategory: value || "" }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Category"
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={form.itemType}
                    onChange={handleChange("itemType")}
                  >
                    <MenuItem value="food_item">Food item</MenuItem>
                    <MenuItem value="dry_goods">Dry goods</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                size={{ xs: 12, md: 2 }}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(form.preorder)}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          preorder: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Pre-order"
                />
              </Grid>

              {/* <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Tag"
                  value={form.tag}
                  onChange={handleChange("tag")}
                />
              </Grid> */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Description"
                  value={form.description}
                  onChange={handleChange("description")}
                  multiline
                  minRows={3}
                  placeholder="Ingredients, notes, or additional details"
                />
              </Grid>

              {/* === PRICING & STOCK === */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Pricing & stock
                </Typography>
                <Divider sx={{ mt: 1 }} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Price"
                  type="number"
                  value={form.price}
                  onChange={handleChange("price")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₱</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Currency"
                  value={form.currency}
                  onChange={handleChange("currency")}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Available quantity (optional)"
                  type="number"
                  value={form.quantity}
                  onChange={handleChange("quantity")}
                />
              </Grid>

              {/* === VARIANTS === */}
              <Grid size={{ xs: 12 }}>
                {/* <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Variants
                </Typography> */}
                <Divider sx={{ mt: 1, mb: 1 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.hasVariants}
                      onChange={handleVariantsToggle}
                    />
                  }
                  label="Variants"
                />
                <Divider sx={{ mt: 1, mb: 1 }} />
              </Grid>
              {form.hasVariants && (
                <>
                  {!form.variants.length && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary">
                        No variants yet. Add one to start.
                      </Typography>
                    </Grid>
                  )}
                  {form.variants.map((variant, variantIndex) => (
                    <Grid key={`variant-${variantIndex}`} size={{ xs: 12 }}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <Autocomplete
                              freeSolo
                              options={variantNameSuggestions}
                              value={variant.name || ""}
                              onChange={(_, value) => {
                                setVariantField(
                                  variantIndex,
                                  "name",
                                  value || "",
                                );
                                if (value) {
                                  handleVariantNameSelected(
                                    variantIndex,
                                    value,
                                  );
                                }
                              }}
                              onInputChange={(_, value, reason) => {
                                setVariantField(
                                  variantIndex,
                                  "name",
                                  value || "",
                                );
                                if (reason === "input" || reason === "clear") {
                                  loadRemoteVariantNames();
                                }
                              }}
                              onOpen={() => {
                                loadRemoteVariantNames();
                              }}
                              filterOptions={(options, params) => {
                                return getFilteredVariantNames(
                                  params.inputValue,
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  label="Variant name"
                                  placeholder="e.g. Choice of softdrink"
                                  size="small"
                                  margin="dense"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {remoteVariantNamesLoading ? (
                                          <CircularProgress
                                            color="inherit"
                                            size={16}
                                          />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          {/* <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Variant type</InputLabel>
                              <Select
                                label="Variant type"
                                value={variant.type}
                                onChange={handleVariantChange(
                                  variantIndex,
                                  "type",
                                )}
                              >
                                <MenuItem value="primary">Primary</MenuItem>
                                <MenuItem value="addon_option">Add-on</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid> */}
                          <Grid
                            size={{ xs: 12, md: 2 }}
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(variant.required)}
                                  onChange={(event) =>
                                    setVariantField(
                                      variantIndex,
                                      "required",
                                      event.target.checked,
                                    )
                                  }
                                />
                              }
                              label="Required"
                            />
                          </Grid>
                          <Grid
                            size={{ xs: 12, md: 3 }}
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Button
                              color="error"
                              variant="outlined"
                              size="small"
                              onClick={() => handleRemoveVariant(variantIndex)}
                            >
                              Remove variant
                            </Button>
                          </Grid>
                          {hasOptionListType(variant.type) && (
                            <Grid size={{ xs: 12, md: 4 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Selection mode</InputLabel>
                                <Select
                                  label="Selection mode"
                                  value={variant.selectionMode || "single"}
                                  onChange={handleVariantChange(
                                    variantIndex,
                                    "selectionMode",
                                  )}
                                >
                                  <MenuItem value="single">
                                    Single select (radio)
                                  </MenuItem>
                                  <MenuItem value="multiple">
                                    Multiple select (checkboxes)
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          )}
                          {isSinglePriceType(variant.type) && (
                            <Grid size={{ xs: 12, md: 4 }}>
                              <TextField
                                fullWidth
                                required
                                label="Add-on price"
                                type="number"
                                value={variant.price || ""}
                                onChange={(event) =>
                                  setVariantField(
                                    variantIndex,
                                    "price",
                                    event.target.value,
                                  )
                                }
                                size="small"
                                margin="dense"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      ₱
                                    </InputAdornment>
                                  ),
                                }}
                                helperText="Add-on fixed variant adds to the base listing price."
                              />
                            </Grid>
                          )}
                          {hasOptionListType(variant.type) && (
                            <>
                              <Grid size={{ xs: 12 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {normalizeVariantType(variant.type) ===
                                  "primary"
                                    ? "Primary variant options"
                                    : "Add-on variant options"}
                                </Typography>
                              </Grid>
                              {(variant.options || []).map(
                                (option, optionIndex) => (
                                  <Fragment
                                    key={`variant-${variantIndex}-option-${optionIndex}`}
                                  >
                                    <Grid size={{ xs: 12, md: 5 }}>
                                      <Autocomplete
                                        freeSolo
                                        options={optionNameSuggestions}
                                        value={option.name || ""}
                                        onChange={(_, value) => {
                                          setVariantOptionField(
                                            variantIndex,
                                            optionIndex,
                                            "name",
                                            value || "",
                                          );
                                          if (value) {
                                            handleOptionNameSelected(
                                              variantIndex,
                                              optionIndex,
                                              value,
                                            );
                                          }
                                        }}
                                        onInputChange={(_, value, reason) =>
                                          setVariantOptionField(
                                            variantIndex,
                                            optionIndex,
                                            "name",
                                            value || "",
                                          )
                                        }
                                        filterOptions={(options, params) => {
                                          return getFilteredOptionNames(
                                            params.inputValue,
                                          );
                                        }}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            fullWidth
                                            label="Option name"
                                            placeholder="e.g. Iced Tea Large"
                                            size="small"
                                            margin="dense"
                                            InputProps={{
                                              ...params.InputProps,
                                              endAdornment: (
                                                <>
                                                  {variantSuggestionsLoading ? (
                                                    <CircularProgress
                                                      color="inherit"
                                                      size={16}
                                                    />
                                                  ) : null}
                                                  {
                                                    params.InputProps
                                                      .endAdornment
                                                  }
                                                </>
                                              ),
                                            }}
                                          />
                                        )}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                      <TextField
                                        fullWidth
                                        label="Price"
                                        type="number"
                                        value={option.price}
                                        onChange={handleVariantOptionChange(
                                          variantIndex,
                                          optionIndex,
                                          "price",
                                        )}
                                        size="small"
                                        margin="dense"
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              ₱
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    </Grid>
                                    <Grid
                                      size={{ xs: 12, md: 3 }}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Button
                                        color="error"
                                        variant="text"
                                        size="small"
                                        onClick={() =>
                                          handleRemoveVariantOption(
                                            variantIndex,
                                            optionIndex,
                                          )
                                        }
                                      >
                                        Remove option
                                      </Button>
                                    </Grid>
                                  </Fragment>
                                ),
                              )}
                              <Grid size={{ xs: 12 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    handleAddVariantOption(variantIndex)
                                  }
                                >
                                  Add option
                                </Button>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                  <Grid size={{ xs: 12 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddVariant}
                    >
                      Add variant
                    </Button>
                    {hasInvalidVariantPrice && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        Each Add-on variant (fixed) must have a valid price.
                      </Typography>
                    )}
                  </Grid>
                </>
              )}

              {/* === PHOTOS === */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Listing photos
                </Typography>
                <Divider sx={{ mt: 1 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Featured photo
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      width: "100%",
                      aspectRatio: "4 / 3",
                      borderRadius: 1,
                      overflow: "hidden",
                      border: "1px solid #e0e0e0",
                      backgroundColor: "#fafafa",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {form.featuredPhotoUrl ? (
                      <Box
                        component="img"
                        src={buildPhotoUrl(form.featuredPhotoUrl)}
                        alt="Featured preview"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No photo
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openPhotoDialog({ type: "featured" })}
                    >
                      {form.featuredPhotoUrl ? "Replace" : "Upload"}
                    </Button>
                    {form.featuredPhotoUrl && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          setForm((prev) => ({ ...prev, featuredPhotoUrl: "" }))
                        }
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Grid>
              {/* === PACKAGE ONLY === */}
              {form.type === "package" && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle1" sx={{ mt: 2 }}>
                      Package components
                    </Typography>
                    <Divider sx={{ mt: 1 }} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Component listing</InputLabel>
                      <Select
                        label="Component listing"
                        value={componentDraft.componentListingId}
                        onChange={handleComponentChange("componentListingId")}
                      >
                        {listings
                          .filter((listing) => listing.type !== "package")
                          .map((listing) => (
                            <MenuItem key={listing.id} value={listing.id}>
                              {listing.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Quantity required"
                      value={componentDraft.quantityRequired}
                      onChange={handleComponentChange("quantityRequired")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Minimum required"
                      value={componentDraft.minimumRequired}
                      onChange={handleComponentChange("minimumRequired")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Add-ons required (comma separated)"
                      value={componentDraft.addonsRequired}
                      onChange={handleComponentChange("addonsRequired")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Add-ons optional (comma separated)"
                      value={componentDraft.addonsOptional}
                      onChange={handleComponentChange("addonsOptional")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Button variant="outlined" onClick={handleAddComponent}>
                      Add component
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            px: { xs: 1.5, sm: 2.5 },
            py: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            position: { xs: "sticky", sm: "static" },
            bottom: 0,
            bgcolor: "background.paper",
          }}
        >
          {!editingId && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={addMore}
                  onChange={(event) => setAddMore(event.target.checked)}
                />
              }
              label="Add more"
            />
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleCloseForm}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isValid || loading || hasInvalidVariantPrice}
            >
              {editingId ? "Update listing" : "Save listing"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                size="small"
                placeholder="Search listings…"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={vendorFilterList}
                value={selectedVendorFilter}
                onChange={(_, value) => setVendorFilterId(value?.id || "")}
                inputValue={vendorFilterInput}
                onInputChange={(_, value) => setVendorFilterInput(value)}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options) => options}
                loading={vendorFilterLoading || vendorsLoading}
                noOptionsText={
                  vendorFilterInput.trim()
                    ? "No vendors found"
                    : "No vendors available"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="All vendors"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                freeSolo
                options={listingCategoryFilterOptions}
                value={categoryFilter}
                onInputChange={(_, value, reason) => {
                  if (!vendorFilterId) return;
                  if (
                    reason === "input" ||
                    reason === "selectOption" ||
                    reason === "clear"
                  ) {
                    const trimmedValue = value?.trim() || "";
                    setCategoryFilter(trimmedValue);
                    if (
                      trimmedValue &&
                      !listingCategoryFilterOptions.includes(trimmedValue)
                    ) {
                      setListingCategoryFilterOptions((prev) =>
                        [...prev, trimmedValue].sort(),
                      );
                    }
                  }
                }}
                disabled={!vendorFilterId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={
                      vendorFilterId ? "All categories" : "Select vendor first"
                    }
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl size="small" fullWidth>
                <Select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All statuses</MenuItem>
                  {LISTING_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        {Boolean(vendorFilterId) && (
          <Box
            sx={{
              px: 2,
              pt: 1,
              pb: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {isSortEnabled && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Drag rows to reorder the vendor menu.
                  {reorderSaving ? " Saving order..." : ""}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleAutoSort}
                  disabled={reorderSaving}
                >
                  Auto-sort
                </Button>
              </>
            )}
            {/* Reordering the categories themselves — the strip customers see
                on the marketplace vendor screen. Only needs a vendor, not a
                category, unlike the per-listing drag above. */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<SwapVertIcon />}
              onClick={() => setCategoryOrderOpen(true)}
            >
              Arrange categories
            </Button>
          </Box>
        )}
        {selectedIds.size > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "action.selected",
            }}
          >
            <Typography variant="body2">
              {selectedIds.size} selected
            </Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={bulkStatus}
                onChange={(event) => setBulkStatus(event.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Set status…
                </MenuItem>
                {LISTING_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              onClick={handleBulkStatusUpdate}
              disabled={!bulkStatus || bulkUpdating}
            >
              {bulkUpdating ? "Applying…" : "Apply"}
            </Button>
            <Button
              size="small"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear selection
            </Button>
          </Box>
        )}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            sx={{
              minWidth: { xs: 0, md: 980 },
              "& th, & td": {
                whiteSpace: { xs: "normal", md: "nowrap" },
                py: 0.45,
                px: 0.75,
              },
              "& .MuiButton-root": { minWidth: 0, px: 0.75, py: 0.2 },
              "& .MuiChip-root": { height: 20 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={
                      selectedIds.size > 0 &&
                      selectedIds.size < displayedListings.length
                    }
                    checked={
                      displayedListings.length > 0 &&
                      displayedListings.every((l) => selectedIds.has(l.id))
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>#</TableCell>
                {isSortEnabled && (
                  <TableCell sx={{ width: 40 }} title="Drag to reorder">
                    ⇅
                  </TableCell>
                )}
                <TableCell sx={{ minWidth: 200 }}>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Category</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedListings.map((listing, index) => (
                <TableRow
                  key={listing.id}
                  draggable={isSortEnabled}
                  onDragStart={(event) => {
                    if (!isSortEnabled) return;
                    setDraggingListingId(listing.id);
                    setDragOverListingId(listing.id);
                    event.dataTransfer.setData("text/plain", listing.id);
                    event.dataTransfer.effectAllowed = "move";
                  }}
                  onDragEnter={() => {
                    if (!isSortEnabled || !draggingListingId) return;
                    setDragOverListingId(listing.id);
                  }}
                  onDragOver={(event) => {
                    if (!isSortEnabled || !draggingListingId) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(event) => {
                    if (!isSortEnabled || !draggingListingId) return;
                    event.preventDefault();
                    const sourceId =
                      event.dataTransfer.getData("text/plain") ||
                      draggingListingId;
                    moveListing(sourceId, listing.id);
                    setDraggingListingId(null);
                    setDragOverListingId(null);
                  }}
                  onDragEnd={() => {
                    setDraggingListingId(null);
                    setDragOverListingId(null);
                  }}
                  sx={{
                    ...(listing.id === draggingListingId
                      ? { opacity: 0.5 }
                      : {}),
                    ...(listing.id === dragOverListingId &&
                    listing.id !== draggingListingId
                      ? {
                          outline: "2px solid #1976d2",
                          outlineOffset: "-2px",
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                        }
                      : {}),
                    ...(!listing.subCategory
                      ? {
                          backgroundColor: "rgba(255, 243, 224, 0.7)",
                        }
                      : {}),
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(listing.id)}
                      onChange={() => handleSelectRow(listing.id)}
                    />
                  </TableCell>
                  <TableCell>
                    {isSortEnabled ? index + 1 : page * rowsPerPage + index + 1}
                  </TableCell>
                  {isSortEnabled && (
                    <TableCell
                      sx={{
                        cursor: "grab",
                        color: "text.secondary",
                        userSelect: "none",
                      }}
                      title="Drag to reorder"
                    >
                      ≡
                    </TableCell>
                  )}
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 200,
                      }}
                    >
                      {listing.featuredPhotoUrl ? (
                        <Box
                          component="img"
                          src={buildPhotoUrl(listing.featuredPhotoUrl)}
                          alt={listing.name}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            objectFit: "cover",
                            border: "1px solid #e0e0e0",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            border: "1px dashed #e0e0e0",
                            backgroundColor: "#fafafa",
                          }}
                        />
                      )}
                      <Typography variant="body2">{listing.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      tone={
                        listing.status === "active"
                          ? "success"
                          : listing.status === "archived"
                            ? "muted"
                            : listing.status === "pending"
                              ? "warning"
                              : "neutral"
                      }
                      label={listing.status}
                    />
                  </TableCell>
                  <TableCell>{listing.subCategory || "—"}</TableCell>
                  <TableCell>
                    {listing.currency} {listing.price}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      <Button size="small" onClick={() => handleEdit(listing)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => requestDelete(listing)}
                      >
                        Delete
                      </Button>
                      {listing.type === "package" && (
                        <Button
                          size="small"
                          onClick={() => openCloneDialog(listing)}
                        >
                          Duplicate as package
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredListings.length && (
                <TableRow>
                  <TableCell colSpan={isSortEnabled ? 8 : 7} sx={{ p: 0 }}>
                    <EmptyState
                      title="No listings yet"
                      description="Add your first listing or adjust filters to see results."
                      dense
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!isSortEnabled && (
          <TablePagination
            component="div"
            count={totalCount}
            size="small"
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </Paper>

      <Dialog
        open={cloneOpen}
        onClose={() => setCloneOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Duplicate package</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="New package name"
                value={cloneForm.name}
                onChange={handleCloneChange("name")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={cloneForm.price}
                onChange={handleCloneChange("price")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Currency"
                value={cloneForm.currency}
                onChange={handleCloneChange("currency")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCloneSave}>
            Save package
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload photo</DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={photoMode}
            onChange={(_, value) => setPhotoMode(value)}
            sx={{ mb: 2 }}
          >
            <Tab value="browse" label="Browse" />
            <Tab value="url" label="Online image URL" />
          </Tabs>
          {photoMode === "browse" && (
            <Box>
              <Button variant="outlined" component="label">
                Choose file
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) =>
                    setPhotoFile(event.target.files?.[0] || null)
                  }
                />
              </Button>
              {photoFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {photoFile.name}
                </Typography>
              )}
            </Box>
          )}
          {photoMode === "url" && (
            <TextField
              fullWidth
              label="Online image URL"
              value={photoUrl}
              onChange={(event) => setPhotoUrl(event.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoDialogOpen(false)}>Cancel</Button>
          {photoMode === "browse" ? (
            <Button
              variant="contained"
              onClick={handleUploadFile}
              disabled={!photoFile || photoUploading}
            >
              Upload
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleImportUrl}
              disabled={!photoUrl.trim() || photoUploading}
            >
              Upload from URL
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        submitting={deleting}
        error={deleteError}
        title="Delete listing?"
        description={
          pendingDelete
            ? `${pendingDelete.name} will be permanently removed. Any vendor links to this listing will also be cleared.`
            : ""
        }
        confirmLabel="Delete"
        confirmColor="error"
      />

      <CategoryOrderDialog
        open={categoryOrderOpen}
        vendorId={vendorFilterId}
        vendorName={selectedVendorFilter?.name}
        onClose={() => setCategoryOrderOpen(false)}
      />
    </PageContainer>
  );
}
